import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { Service } from "@oathera/contract";
import {
  createStreamViewStore,
  type StreamViewSnapshot,
  type StreamViewStore,
} from "./stream-view.ts";
import { useLiveStreamStore } from "./live-stream.tsx";
import { useTimeRange } from "./time-range.tsx";
import { StreamClient } from "../lib/stream/client.ts";
import type { StreamTopic } from "../lib/stream/frames.ts";

/**
 * React context + hooks over the view store (`stream-view.ts`), and the driver that runs the stream
 * client (`lib/stream/client.ts`).
 *
 * The provider owns one view store for the app. The screens read it with `useStreamView` (the whole
 * snapshot) or the focused selectors below. The CLIENT is started by `useLiveStreamDriver`, mounted
 * once near the app root: it creates a `StreamClient`, wires it to both stores, starts it, and
 * mirrors the time-range pause state into it so pausing the live toggle buffers deltas and resuming
 * replays them (FR-13 AC2). Keeping the driver as a hook (not inside the provider) lets a screen or
 * test mount the provider without opening a socket.
 */

const StreamViewContext = createContext<StreamViewStore | null>(null);

export function StreamViewProvider(props: {
  children: ReactNode;
  /** Inject a pre-seeded store for a test; defaults to a fresh one. */
  store?: StreamViewStore;
}): ReactNode {
  const store = props.store ?? defaultStore();
  return <StreamViewContext.Provider value={store}>{props.children}</StreamViewContext.Provider>;
}

let singleton: StreamViewStore | null = null;
function defaultStore(): StreamViewStore {
  singleton ??= createStreamViewStore();
  return singleton;
}

/** The view store itself (for the driver). */
export function useStreamViewStore(): StreamViewStore {
  const store = useContext(StreamViewContext);
  if (store === null) throw new Error("useStreamViewStore must be used within <StreamViewProvider>");
  return store;
}

/** A reactive snapshot of the whole view store. */
export function useStreamView(): StreamViewSnapshot {
  const store = useStreamViewStore();
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

/** The buffered-delta count ("N buffered"); re-renders only when it changes. */
export function useBufferedCount(): number {
  return useStreamView().bufferedCount;
}

/** Whether a gap has been disclosed (overflow → fresh snapshot recovery; design 12.3). */
export function useGapDisclosed(): boolean {
  return useStreamView().gapDisclosed;
}

export interface LiveStreamDriverOptions {
  /** Subscribe to this service only, or null/undefined for all services. */
  service?: Service | null;
  /** Topics to subscribe to; defaults to all the hub carries. */
  topics?: readonly StreamTopic[];
  /**
   * When false, the driver does not open the stream (e.g. the shell is still showing the cold-core
   * "waking" state, or no tenant is resolved yet). Defaults to true.
   */
  enabled?: boolean;
}

/**
 * Run the live-stream client for the app's lifetime. Mount this ONCE near the root (inside the
 * providers). It creates the client, wires it to the connection store and the view store, starts
 * it, and keeps its pause state in step with the time-range live toggle.
 *
 * The client is created once (held in a ref) so a re-render does not reconnect; it is stopped on
 * unmount. The subscription parameters (service/topics) are read on creation; a change to them (a
 * tenant/service switch) tears the client down and starts a fresh one so the new subscription takes
 * effect with its own snapshot.
 */
export function useLiveStreamDriver(options: LiveStreamDriverOptions = {}): void {
  const liveStore = useLiveStreamStore();
  const viewStore = useStreamViewStore();
  const { live } = useTimeRange();

  const service = options.service ?? null;
  const topics = options.topics;
  const enabled = options.enabled ?? true;
  const topicsKey = topics === undefined ? "*" : [...topics].join(",");

  const clientRef = useRef<StreamClient | null>(null);

  // The latest pause state, held in a ref so the connection effect can read it on (re)start WITHOUT
  // depending on `live` — toggling pause must not recreate the client (that would drop the socket).
  const pausedRef = useRef(!live);
  pausedRef.current = !live;

  // Create/replace the client when the subscription identity or the enabled flag changes. `topics`
  // is folded into `topicsKey` for the dependency so an array identity change does not reconnect.
  useEffect(() => {
    if (!enabled) return undefined;
    void topicsKey; // the stable key is the real dependency; `topics` is read for its value only
    const client = new StreamClient({
      liveStore,
      viewStore,
      service,
      ...(topics === undefined ? {} : { topics }),
    });
    clientRef.current = client;
    // Reflect the current pause state immediately so a client started while paused buffers.
    client.setPaused(pausedRef.current);
    client.start();
    return () => {
      client.stop();
      if (clientRef.current === client) clientRef.current = null;
    };
  }, [liveStore, viewStore, service, topics, topicsKey, enabled]);

  // Push pause/resume into the running client without reconnecting.
  useEffect(() => {
    clientRef.current?.setPaused(!live);
  }, [live]);
}
